import {
  type BagEntry,
  type ItemInstance,
  type ItemTemplate,
  planStashPlacement,
  stashBlock,
} from '@shefira/shared';

/**
 * What the chest's window (ADR 0079) previews before a move, with the shared
 * rules: whether the server would refuse it, and how many cases a grid
 * shows. A preview only; the server judges every move all the same.
 */

export type StashSide = 'chest' | 'bag';
export type StashBlockReason = 'far' | 'bound' | 'wardrobe' | 'full';

/** Where a side's items would go: the other side's entries and size. */
export interface StashTarget {
  entries: readonly BagEntry[];
  size: number;
}

/** Why moving `quantity` of this item to the other side would be refused, or null. */
export function stashMoveBlock(
  side: StashSide,
  item: ItemInstance,
  template: ItemTemplate,
  quantity: number,
  target: StashTarget,
  inReach: boolean,
): StashBlockReason | null {
  // Only the bag holds what may not go in: the chest never took it.
  const block = side === 'bag' ? stashBlock(item, template) : null;
  if (block !== null) return block;
  if (!inReach) return 'far';
  if (planStashPlacement(target.entries, template, quantity, target.size) === null) return 'full';
  return null;
}

/**
 * The cases a grid shows: its capacity, or more when an item sits beyond it
 * (a capacity that went down still shows what overflows, ADR 0079).
 */
export function stashCells(entries: readonly BagEntry[], capacity: number): number {
  return entries.reduce((most, entry) => Math.max(most, entry.slot + 1), capacity);
}
